/**
 * Per `fan-game-bridge` — external mini-game integration (first game:
 * A1Lab Soccer Arena). The platform implements the game's "Platform API
 * Interface v1": launch-token exchange, owned-character listing, and
 * idempotent match-result ingest. Platform owns ALL progression math —
 * games never send affinity deltas.
 */
import { z } from 'zod';
// ── Game registry row (`nx-games-{env}`) ────────────────────────────
export const GameRewardPolicySchema = z.object({
    /** 親密度加成 per win / per loss(draw 依 loss)。phase 1 無點數獎勵。 */
    winAffinity: z.number().int().min(0).max(100).default(1),
    lossAffinity: z.number().int().min(0).max(100).default(0),
    /** 每日(UTC)有獎勵場次上限;超過照收結果、不發獎勵。 */
    dailyMatchCap: z.number().int().min(0).max(1000).default(10),
});
export const GameSchema = z.object({
    gameId: z.string().regex(/^[a-z0-9-]{3,64}$/, 'gameId 需為小寫英數與連字號'),
    name: z.string().min(1).max(80),
    description: z.string().max(500).optional(),
    iconUrl: z.string().url().optional(),
    /** 已部署的啟動連結(https)。origin 由此推導,是 /v1 CORS 與
     *  來源驗證的錨點 — 啟動永遠開這個 URL,不可被偽造改導。 */
    gameUrl: z.string().url().refine((u) => u.startsWith('https://'), 'gameUrl 必須為 https'),
    enabled: z.boolean().default(true),
    /** 'all' 或指定 characterIds — 角色頁「功能」與 /v1 下發皆依此過濾。 */
    characterScope: z.union([z.literal('all'), z.array(z.string()).max(200)]).default('all'),
    rewardPolicy: GameRewardPolicySchema.default({ winAffinity: 1, lossAffinity: 0, dailyMatchCap: 10 }),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const GameCreateBodySchema = GameSchema.omit({ createdAt: true, updatedAt: true });
export const GamePatchBodySchema = GameCreateBodySchema.partial().omit({ gameId: true });
/** 註冊表 gameUrl 的 origin(CORS / 驗證用)。 */
export function gameOriginOf(gameUrl) {
    try {
        return new URL(gameUrl).origin;
    }
    catch {
        return null;
    }
}
// ── Per-character game profile(掛在 character 文件上)────────────
export const GAME_MOTION_KEYS = ['idle', 'walk', 'run', 'shoot', 'tackle'];
export const GameProfileSchema = z.object({
    rarity: z.enum(['R', 'SR', 'SSR']).optional(),
    roles: z.array(z.enum(['goalkeeper', 'attacker'])).min(1).optional(),
    /** clamp 0.5..2(規格書);未設 → 1。 */
    scale: z.number().min(0.5).max(2).optional(),
    /** motion key → clipName 覆寫(優先於 clipLabels 啟發式)。 */
    motionOverrides: z.record(z.enum(GAME_MOTION_KEYS), z.string()).optional(),
    // 系統快取(UI 不顯示):GLB sha256 + 其對應的 modelPath 指紋。
    modelSha256: z.string().length(64).optional(),
    modelSha256Of: z.string().optional(),
    /** 系統快取:GLB byte size(25MiB 過濾用)。 */
    modelBytes: z.number().int().optional(),
});
export const GAME_DEFAULT_RARITY = 'R';
export const GAME_DEFAULT_ROLES = ['goalkeeper', 'attacker'];
export const GAME_GLB_MAX_BYTES = 25 * 1024 * 1024;
// ── /v1 request/response schemas(遊戲方規格書 v1)─────────────────
export const GameExchangeBodySchema = z.object({ gameId: z.string().min(1) });
export const GameResultBodySchema = z.object({
    matchId: z.string().min(1).max(128),
    playerId: z.string().min(1),
    mode: z.string().min(1).max(40),
    outcome: z.enum(['win', 'loss', 'draw']),
    score: z.object({ player: z.number().int().min(0), opponent: z.number().int().min(0) }),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime(),
    characters: z.array(z.object({
        characterId: z.string().min(1),
        slot: z.string().min(1).max(40),
    })).min(1).max(10),
});
/** Map clipLabels(clipName→label)+ overrides 成規格書 motions。
 *  idle/walk/run 直接找同名 label;shoot/tackle 啟發式(踢/射/鏟/kick/
 *  shoot/tackle);查無 → null(規格允許)。 */
export function resolveGameMotions(clipLabels, overrides) {
    const byLabel = new Map();
    for (const [clip, label] of Object.entries(clipLabels ?? {})) {
        const norm = label.trim().toLowerCase();
        if (!byLabel.has(norm))
            byLabel.set(norm, clip);
    }
    // Operators label clips in Chinese as often as English — direct
    // label==key match only covers the English case, so every key gets a
    // bilingual heuristic fallback (overrides always win).
    const heuristics = {
        idle: /(idle|stand|站|待機|自然|閒置|發呆)/i,
        walk: /(walk|走)/i,
        run: /(run|跑|奔)/i,
        shoot: /(shoot|kick|射|踢)/i,
        tackle: /(tackle|slide|鏟|撲)/i,
    };
    const find = (key) => {
        const override = overrides?.[key];
        if (override)
            return override;
        const direct = byLabel.get(key);
        if (direct)
            return direct;
        for (const [label, clip] of byLabel) {
            if (heuristics[key].test(label))
                return clip;
        }
        return null;
    };
    return {
        idle: find('idle'), walk: find('walk'), run: find('run'),
        shoot: find('shoot'), tackle: find('tackle'),
    };
}
/** viewportPose.rotationY(任意度數)→ 規格書 yaw -180..180。 */
export function normalizeYaw(rotationY) {
    if (typeof rotationY !== 'number' || !Number.isFinite(rotationY))
        return 0;
    let y = rotationY % 360;
    if (y > 180)
        y -= 360;
    if (y < -180)
        y += 360;
    return Math.round(y * 100) / 100;
}
